import { BookingType } from "PFApp/booking/components/booking_form/booking_type";
import { DetailsPanelType } from "PFApp/booking/components/details_panel/details_panel.types";
import { useDetailsPanelsActions } from "PFApp/booking/components/details_panel/hooks";
import useBookingModulePermissions from "PFApp/use_booking_module_permissions";
import { OverbookedDates, OverbookingsManagementMode } from "PFCore/services/bookings_suggested_changes";
import { BasicProfile, Booking, BookingCategory } from "PFTypes";
import { useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useUnmount } from "react-use";

import { BookingSelectOption } from "../booking_questions/booking_questions.types";
import { GroupBookingsQuestionsOption } from "../booking_questions/group_bookings_questions.types";
import { UpdateQuestionsModal } from "../booking_questions/update_questions_modal";
import { OverbookingsManagementModal } from "../overbookings_management_modal/overbookings_management_modal";
import { bookingFormValuesToSuggestedChangesData } from "../overbookings_management_modal/overbookings_management_modal.utils";
import { BookingForm, BookingFormProps } from "./booking_form";
import { BookingDataItem, BookingFormDataCreateMode, BookingFormMode } from "./booking_form.types";
import { isEditMode } from "./booking_form.utils";
import { useInitialActivityId, useInitialBookingType, useInitialGlobalCategory } from "./initial_data";
import { useApplySuggestedChanges } from "./use_apply_suggested_changes";
import { useHandleSubmit } from "./use_handle_submit";
import { OnSuccessData } from "./use_handle_submit/use_handle_submit";
import { GroupBookingOption } from "./workforce_member_select/workforce_member_select.utils";

const DEFAULT_FORM_VALUES = {
  updateGroupOptionSelected: GroupBookingsQuestionsOption.GroupThis,
  updateRepeatOptionSelected: BookingSelectOption.All,
  globalOverridesDiaryTime: true,
  globalOverridesNonWorkingTime: false
};

export type BookingFormBasicProfile = Omit<BasicProfile, "email" | "avatar"> &
  Partial<Pick<BasicProfile, "email" | "avatar">>;

type BookingFormWorkforceMember = BookingFormBasicProfile | GroupBookingOption;

export type BookingFormValues = {
  workforceMember?: BookingFormWorkforceMember;
  activityId?: number;
  bookingType: BookingType;
  bookings: BookingDataItem[];
  globalTitle?: string;
  globalDescription?: string;
  globalCategory?: BookingCategory | null;
  globalOverridesDiaryTime: boolean;
  globalOverridesNonWorkingTime: boolean;
  updateGroupOptionSelected: GroupBookingsQuestionsOption;
  updateRepeatOptionSelected: BookingSelectOption;
};

type OverbookingsManagementModalState = {
  isOpen: boolean;
  mode: OverbookingsManagementMode | null;
  overbookedDates: OverbookedDates;
};

const INITIAL_OVERBOOKINGS_MANAGEMENT_MODAL_STATE: OverbookingsManagementModalState = {
  isOpen: false,
  mode: null,
  overbookedDates: []
};

type BookingFormProviderProps = Omit<
  BookingFormProps,
  | "onOpenOverbookingsManagementModal"
  | "datesModifiedByUser"
  | "setDatesModifiedByUser"
  | "loadingState"
  | "setLoadingState"
  | "initialBookingsRef"
> & {
  initialBooking?: Booking;
  onSuccess?: (data?: OnSuccessData) => Promise<void>;
  onPreSubmitAction?: (shortlistId: number | undefined) => Promise<void>;
};

export const BookingFormProvider = ({
  show,
  zIndex,
  initialData = {},
  initialBooking,
  onSuccess,
  mode,
  source,
  bookingModuleDetails,
  activeBookingIndex,
  setActiveBookingIndex,
  onPreSubmitAction
}: BookingFormProviderProps) => {
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showOverbookingsManagementModal, setShowOverbookingsManagementModal] =
    useState<OverbookingsManagementModalState>(INITIAL_OVERBOOKINGS_MANAGEMENT_MODAL_STATE);
  const [loadingState, setLoadingState] = useState<Record<string, boolean>>({});
  const initialBookingsRef = useRef<BookingDataItem[] | null>(null);

  const { closeBookingFormSidePanel } = useDetailsPanelsActions();
  const { closeDetailsPanel } = useDetailsPanelsActions();

  const { isBookingResourcer } = useBookingModulePermissions();

  const isCreateMode = mode === BookingFormMode.Create;

  const methods = useForm<BookingFormValues>({
    defaultValues: DEFAULT_FORM_VALUES
  });

  useUnmount(() => {
    bookingModuleDetails?.updateOverbookedBookings?.(null);
    methods.reset();
  });

  useInitialActivityId({
    setValue: methods.setValue
  });
  useInitialGlobalCategory({
    enabled: isCreateMode,
    setValue: methods.setValue,
    bookingCategoryId: isCreateMode ? (initialData as BookingFormDataCreateMode).bookingCategoryId : undefined
  });
  useInitialBookingType({
    initialBooking,
    setValue: methods.setValue
  });

  const handleClose = () => {
    bookingModuleDetails?.updateOverbookedBookings?.(null);
    methods.reset();
    closeBookingFormSidePanel();
  };

  const { handleSubmit } = useHandleSubmit({
    initialData,
    mode,
    onSuccess: (data) => {
      onSuccess?.(data);
      handleClose();
      // close Overbookings side panel after successful action as it may display outdated data
      initialBooking && closeDetailsPanel(DetailsPanelType.Overbookings, initialBooking.profile_id);
    },
    onError: () => handleClose(),
    formMethods: methods,
    setLoadingState,
    onPreSubmitAction
  });

  const handlePreSubmitActions = async (values: BookingFormValues) => {
    const { bookingType } = methods.getValues();
    const isGrouped = !!initialBooking?.booking_group_id;
    const isRepeated = bookingType === BookingType.Repeated;

    if (isEditMode(mode) && (isGrouped || isRepeated) && isBookingResourcer) {
      setShowQuestionsModal(true);
    } else {
      await handleSubmit(values);
    }
  };

  const handleFormSubmit = methods.handleSubmit(handlePreSubmitActions);
  const handleConfirmSubmit = methods.handleSubmit(handleSubmit);

  const handleCloseOverbookingsManagementModal = () => {
    setShowOverbookingsManagementModal(INITIAL_OVERBOOKINGS_MANAGEMENT_MODAL_STATE);
  };

  const bookingsData = bookingFormValuesToSuggestedChangesData(
    methods.getValues(),
    showOverbookingsManagementModal.overbookedDates,
    showOverbookingsManagementModal.mode
  );

  const { handleApplySuggestedChanges } = useApplySuggestedChanges({
    mode: showOverbookingsManagementModal.mode,
    handleSubmit,
    formMethods: methods
  });

  return (
    <FormProvider {...methods}>
      <form id="booking_form" onSubmit={handleFormSubmit}>
        <BookingForm
          show={show}
          zIndex={zIndex}
          initialData={initialData}
          mode={mode}
          onClose={handleClose}
          source={source}
          bookingModuleDetails={bookingModuleDetails}
          onOpenOverbookingsManagementModal={(mode, overbookedDates) => {
            setShowOverbookingsManagementModal({
              isOpen: true,
              mode,
              overbookedDates
            });
          }}
          loadingState={loadingState}
          setLoadingState={setLoadingState}
          activeBookingIndex={activeBookingIndex}
          setActiveBookingIndex={setActiveBookingIndex}
          initialBookingsRef={initialBookingsRef}
        />
      </form>
      {showQuestionsModal && (
        <UpdateQuestionsModal
          onClose={() => setShowQuestionsModal(false)}
          onConfirm={handleConfirmSubmit}
          initialBooking={initialBooking}
        />
      )}
      {showOverbookingsManagementModal.isOpen && (
        <OverbookingsManagementModal
          onClose={handleCloseOverbookingsManagementModal}
          onConfirm={handleApplySuggestedChanges}
          category={methods.getValues().globalCategory ?? bookingsData?.bookings[0]?.category}
          bookingsData={bookingsData}
        />
      )}
    </FormProvider>
  );
};
